/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { Trans, useTranslation } from 'next-i18next';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import HeroSVG from '@toucan/images/bird.svg';

import { Link, LinkSize } from '@toucan/client/shared/components/link';

import { styles } from './sign-up-confirmation-pending.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SignUpConfirmationPendingProps {
    /**
     * The email address of the user who is signing up.
     */
    email: string;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Handle resending account verification email
     */
    onResendEmail: (email: string) => void;
}

export type SignUpConfirmationPendingStyleProps = Omit<
    SignUpConfirmationPendingProps,
    'onResendEmail'
>;

const StyledSignUpConfirmationPending = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})<SignUpConfirmationPendingStyleProps>`
    ${styles}
`;

export const SignUpConfirmationPending: FunctionComponent<
    SignUpConfirmationPendingProps
> = ({
    email,
    onResendEmail,
    ...otherProps
}: SignUpConfirmationPendingProps) => {
    const { t } = useTranslation();
    return (
        <StyledSignUpConfirmationPending email={email} {...otherProps}>
            <div
                className="hero-image"
                data-testid="confirmation-pending__hero"
            >
                <HeroSVG className="hero-svg" />
            </div>
            <Heading
                size={HeadingSize.H1}
                data-testid="confirmation-pending__title"
                className="heading"
            >
                {t('Hang on, before you go!')}
            </Heading>
            <p>{t('Please verify your account.')}</p>
            <p>
                {t(
                    'In a few moments - perhaps even right now - you’ll get an email notification from MPB.'
                )}
            </p>
            <p>
                <Trans components={[<strong key={0}>$1</strong>]}>
                    {t(
                        '<0>Simply tap the link in the email</0> so we know it’s really you. Safety first. Then you’re all set.'
                    )}
                </Trans>
            </p>
            <p>{t('What if the email isn’t in my inbox? 😔')}</p>
            <p>
                <Trans
                    components={[
                        <Link
                            key={0}
                            size={LinkSize.MEDIUM}
                            onClick={() => {
                                onResendEmail(email);
                            }}
                            data-testid="confirmation-pending__resend-email-button"
                        >
                            $1
                        </Link>,
                    ]}
                >
                    {t(
                        'If it doesn’t appear after a couple of minutes, and it hasn’t been caught by an overzealous spam filter, then we can <0>send it again</0>'
                    )}
                </Trans>
            </p>
        </StyledSignUpConfirmationPending>
    );
};
